import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { certificationApi } from '../../services/api';
import './CertificationDashboard.css';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const PILLAR_COLORS = {
  voice_safety: '#10b981',
  emotional_safety: '#06b6d4',
  growth_safety: '#8b5cf6',
  boundary_safety: '#f59e0b',
  identity_safety: '#ec4899'
};

const PILLAR_SVG_ICONS = {
  voice_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
  emotional_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  growth_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  boundary_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  identity_safety: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
};

const StarRating = ({ rating, size = 20 }) => (
  <div className="cd-stars" style={{ gap: size * 0.15 }}>
    {[...Array(5)].map((_, i) => (
      <svg key={i} viewBox="0 0 24 24" width={size} height={size}
        fill={i < rating ? '#f59e0b' : '#e5e7eb'}
        stroke={i < rating ? '#f59e0b' : '#d1d5db'}
        strokeWidth="0.5">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
      </svg>
    ))}
  </div>
);

const CertificationDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [copiedBadge, setCopiedBadge] = useState(false);
  const [orgName, setOrgName] = useState('');
  const pollRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [latest, hist] = await Promise.all([
        certificationApi.getLatest(),
        certificationApi.getHistory()
      ]);
      setData(latest);
      setHistory(hist.history || []);
      if (latest.available && latest.snapshot?.org_name) {
        setOrgName(latest.snapshot.org_name);
      }
    } catch (err) {
      console.error('Failed to load certification data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadData]);

  const handleEvaluate = async () => {
    const name = orgName.trim() || 'Organization';
    setEvaluating(true);
    try {
      await certificationApi.evaluate(name);
      pollRef.current = setInterval(async () => {
        try {
          const latest = await certificationApi.getLatest();
          if (latest.available && latest.snapshot) {
            const prevId = data?.snapshot?.id;
            if (!prevId || latest.snapshot.id !== prevId) {
              setData(latest);
              setEvaluating(false);
              clearInterval(pollRef.current);
              const hist = await certificationApi.getHistory();
              setHistory(hist.history || []);
            }
          }
        } catch {}
      }, 5000);
      setTimeout(() => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          setEvaluating(false);
          loadData();
        }
      }, 300000);
    } catch (err) {
      console.error('Failed to start evaluation:', err);
      setEvaluating(false);
    }
  };

  const handleCopyBadge = () => {
    if (data?.result?.badge_html) {
      navigator.clipboard.writeText(data.result.badge_html);
      setCopiedBadge(true);
      setTimeout(() => setCopiedBadge(false), 2000);
    }
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'excellent';
    if (score >= 65) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  };

  const s = data?.snapshot;
  const pillars = data?.pillars || [];

  const radarData = pillars.length > 0 ? {
    labels: pillars.map(p => p.label),
    datasets: [{
      label: 'Score',
      data: pillars.map(p => p.score),
      backgroundColor: 'rgba(16, 185, 129, 0.12)',
      borderColor: '#10b981',
      borderWidth: 2,
      pointBackgroundColor: pillars.map(p => PILLAR_COLORS[p.pillar]),
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 6
    }]
  } : null;

  const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      r: {
        min: 0, max: 100,
        ticks: { stepSize: 20, color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af', backdropColor: 'transparent', font: { size: 10 } },
        grid: { color: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6' },
        angleLines: { color: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6' },
        pointLabels: { color: isDark ? 'rgba(255,255,255,0.7)' : '#374151', font: { size: 11, weight: '500' } }
      }
    }
  };

  return (
    <div className="cd-page">
      {/* Header */}
      <div className="cd-header">
        <div>
          <h1>PSW Certification</h1>
          <p>Psychologically Safe Workplace™ by Mend</p>
        </div>
        <div className="cd-header-actions">
          <input type="text" className="cd-org-input" placeholder="Organization name..." value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          <button className="cd-eval-btn" onClick={handleEvaluate} disabled={evaluating}>
            {evaluating ? (<><div className="cd-spinner" /> Evaluating...</>) : (<><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> Run Evaluation</>)}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="cd-center-msg"><div className="loading-spinner" /><p>Loading certification data...</p></div>
      ) : error ? (
        <div className="cd-center-msg"><p>Error: {error}</p><button className="cd-eval-btn" onClick={loadData}>Retry</button></div>
      ) : !data?.available ? (
        <div className="cd-center-msg">
          <div className="cd-empty-shield"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
          <h2>No Certification Data Yet</h2>
          <p>Enter your organization name and run an evaluation to get started.</p>
        </div>
      ) : (
        <div className="cd-content">
          {/* Top row */}
          <div className="cd-top-row">
            <div className="cd-card cd-status-card">
              <div className="cd-status-header">
                <span className={`cd-badge cd-badge-${s.status}`}>
                  {s.status === 'certified' ? 'Certified' : s.status === 'not_certified' ? 'Not Certified' : s.status}
                </span>
                {s.valid_until && <span className="cd-valid">Valid until {new Date(s.valid_until).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
              </div>
              <div className="cd-status-center">
                <div className="cd-score-ring">
                  <svg viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="52" fill="none" stroke={isDark ? 'rgba(255,255,255,0.05)' : '#f3f4f6'} strokeWidth="8" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#10b981" strokeWidth="8"
                      strokeDasharray={`${(s.overall_score / 100) * 327} 327`}
                      strokeLinecap="round" transform="rotate(-90 60 60)" />
                  </svg>
                  <div className="cd-score-num">{Math.round(s.overall_score)}</div>
                </div>
                <div className="cd-status-info">
                  <StarRating rating={s.star_rating} size={24} />
                  <span className="cd-star-label">{s.star_rating > 0 ? `${s.star_rating}-Star Certification` : 'Not Yet Rated'}</span>
                  <span className="cd-org-label">{s.org_name}</span>
                  <span className="cd-emp-label">{s.participating_employees}/{s.total_employees} employees</span>
                </div>
              </div>
              {s.certificate_id && (
                <div className="cd-badge-preview">
                  <img src={`/api/certification/badge/${s.certificate_id}`} alt="Badge" className="cd-badge-img" />
                </div>
              )}
            </div>

            <div className="cd-card cd-radar-card">
              <h3>Pillar Overview</h3>
              {radarData && <div className="cd-radar-wrap"><Radar data={radarData} options={radarOptions} /></div>}
            </div>
          </div>

          {/* Pillar cards */}
          <div className="cd-pillars-row">
            {pillars.map(p => (
              <div key={p.pillar} className="cd-card cd-pillar-card" style={{ '--pcolor': PILLAR_COLORS[p.pillar] }}>
                <div className="cd-pillar-top">
                  <div className="cd-pillar-icon" style={{ background: PILLAR_COLORS[p.pillar] + '18', color: PILLAR_COLORS[p.pillar] }}>{PILLAR_SVG_ICONS[p.pillar]}</div>
                  <div>
                    <h4>{p.label}</h4>
                    <span className="cd-pillar-sub">{p.description}</span>
                  </div>
                </div>
                <div className="cd-pillar-score-row">
                  <span className={`cd-pillar-num ${getScoreClass(p.score)}`}>{Math.round(p.score)}</span>
                  <div className="cd-pillar-bar"><div className="cd-pillar-fill" style={{ width: `${p.score}%`, background: PILLAR_COLORS[p.pillar] }} /></div>
                </div>
                {p.evidence_summary && <p className="cd-pillar-evidence">{p.evidence_summary}</p>}
              </div>
            ))}
          </div>

          {/* Actions + History */}
          <div className="cd-bottom-row">
            <div className="cd-card cd-actions-card">
              <h3>Actions</h3>
              <div className="cd-actions-list">
                {s.certificate_id && (
                  <>
                    <a href={`/api/certification/pdf/${s.certificate_id}`} target="_blank" rel="noreferrer" className="cd-action-btn">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Certificate (PDF)
                    </a>
                    <button className="cd-action-btn" onClick={handleCopyBadge}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      {copiedBadge ? 'Copied!' : 'Copy Badge Embed Code'}
                    </button>
                    <button className="cd-action-btn" onClick={() => navigate(`/certified/${s.certificate_id}`)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      View Public Profile
                    </button>
                  </>
                )}
                <button className="cd-action-btn" onClick={() => navigate('/certification')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  View Methodology Page
                </button>
              </div>
            </div>

            {history.length > 0 && (
              <div className="cd-card cd-history-card">
                <h3>Evaluation History</h3>
                <table className="cd-history-table">
                  <thead><tr><th>Date</th><th>Score</th><th>Rating</th><th>Status</th></tr></thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id}>
                        <td>{new Date(h.evaluated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                        <td className="cd-h-score">{Math.round(h.overall_score)}</td>
                        <td><StarRating rating={h.star_rating} size={14} /></td>
                        <td><span className={`cd-badge cd-badge-sm cd-badge-${h.status}`}>{h.status === 'certified' ? 'Certified' : 'Not Certified'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationDashboard;
